/**
 * Recovery for a tab that is running a build that no longer exists.
 *
 * The backend serves the React bundle out of `backend/static/` and Vite gives
 * every chunk a content hash, so a rebuild REPLACES the whole `assets/` folder.
 * A tab opened before the rebuild keeps the old entry bundle in memory (its own
 * hashed file is cached, and `index.html` is never re-read without a reload),
 * and the first `import()` it makes afterwards — a lazy tab like the document
 * form's Body tab, the Jasper workspace, the document-form runtime view — asks
 * for a chunk name that was deleted by the rebuild:
 *
 *     GET /assets/BodyTab-C7BbAvwM.js -> 404
 *     Uncaught TypeError: Failed to fetch dynamically imported module
 *
 * React has no recovery for that: the Suspense boundary throws and the pane
 * stays blank until the user reloads by hand, with a console error that looks
 * like a bug in the tab itself. Nothing is actually broken — the page is simply
 * out of date — so the honest fix is to notice the shape of that failure and
 * reload once, which fetches the fresh `index.html` (served no-cache) and with
 * it the chunk names that do exist.
 *
 * "Once" is the whole safety story: a genuine 404 (a chunk that is missing from
 * a BROKEN build rather than a superseded one) would otherwise reload forever.
 * The stamp lives in session/local storage because the in-memory flag dies with
 * the page we are about to replace; if neither store is usable we log and leave
 * the tab alone rather than risk a loop.
 */

const RELOAD_STAMP_KEY = 'twasta:stale-build-reload'
/** A second failure within this window means the reload did not help. */
const RELOAD_WINDOW_MS = 30_000

/**
 * Messages browsers use when a dynamic import cannot be fetched or parsed.
 * Chrome, Firefox and Safari each word it differently; the last one is what a
 * server answers with when an SPA fallback hands back `index.html` for a
 * missing script path.
 */
const STALE_CHUNK_PATTERNS: RegExp[] = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
  /is not a valid javascript mime type/i,
]

let reloadAttempted = false

function messageOf(value: unknown): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (value instanceof Error) return `${value.message} ${value.stack ?? ''}`
  const maybe = value as { message?: unknown }
  return typeof maybe.message === 'string' ? maybe.message : ''
}

function looksStale(value: unknown): boolean {
  const message = messageOf(value)
  if (!message) return false
  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message))
}

/** Returns the store we can stamp a reload in, or null when none is writable. */
function reloadStore(): Storage | null {
  for (const get of [() => window.sessionStorage, () => window.localStorage]) {
    try {
      const store = get()
      const probe = `${RELOAD_STAMP_KEY}:probe`
      store.setItem(probe, '1')
      store.removeItem(probe)
      return store
    } catch {
      // Private mode, blocked site data — try the next one.
    }
  }
  return null
}

function reloadOnce(reason: string): void {
  if (reloadAttempted) return

  const store = reloadStore()
  if (!store) {
    console.error(
      '[twasta] this page is running an outdated build and browser storage is ' +
        'unavailable, so it cannot be reloaded safely — please reload manually.',
      reason,
    )
    return
  }

  const last = Number(store.getItem(RELOAD_STAMP_KEY) ?? 0)
  if (Number.isFinite(last) && last > 0 && Date.now() - last < RELOAD_WINDOW_MS) {
    console.error(
      '[twasta] a module still failed to load after reloading for a new build — ' +
        'the chunk is genuinely missing, not stale.',
      reason,
    )
    return
  }

  try {
    store.setItem(RELOAD_STAMP_KEY, String(Date.now()))
  } catch {
    // Storage went away between the probe and now: without a stamp a reload
    // could loop, so leave the tab as it is.
    console.error(
      '[twasta] this page is running an outdated build — please reload manually.',
      reason,
    )
    return
  }

  reloadAttempted = true
  console.warn(
    '[twasta] a newer build is deployed; reloading this tab to pick it up.',
    reason,
  )
  window.location.reload()
}

/**
 * Listens for the three ways a dead chunk surfaces: Vite's own preload event,
 * an uncaught error, and a rejected import promise. Call once, before render.
 */
export function installStaleBuildRecovery(): void {
  if (typeof window === 'undefined') return

  // Vite wraps every built dynamic import in `__vitePreload`, which dispatches
  // this (cancelable) event when the network fetch fails. Preventing the
  // default stops Vite from rethrowing while we reload.
  window.addEventListener('vite:preloadError', (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload
    event.preventDefault()
    reloadOnce(messageOf(payload) || 'vite:preloadError')
  })

  window.addEventListener('error', (event: ErrorEvent) => {
    if (looksStale(event.error) || looksStale(event.message)) {
      reloadOnce(event.message || 'window error')
    }
  })

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (looksStale(event.reason)) {
      reloadOnce(messageOf(event.reason) || 'unhandled rejection')
    }
  })
}
